/**
 * Feedback & Suggestions Tab
 * Allow owners to submit feedback, suggestions, and report issues
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { 
  MessageCircle, Send, ThumbsUp, Lightbulb, AlertCircle, 
  HelpCircle, CheckCircle, Clock, History
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEEDBACK_TYPES = [
  { value: "compliment", label: "Compliment / Praise", icon: ThumbsUp, color: "text-green-600", bgColor: "bg-green-50" },
  { value: "suggestion", label: "Suggestion / Feature Request", icon: Lightbulb, color: "text-amber-600", bgColor: "bg-amber-50" },
  { value: "issue", label: "Report an Issue / Bug", icon: AlertCircle, color: "text-red-600", bgColor: "bg-red-50" },
  { value: "question", label: "Question / Support", icon: HelpCircle, color: "text-blue-600", bgColor: "bg-blue-50" },
  { value: "other", label: "Other", icon: MessageCircle, color: "text-purple-600", bgColor: "bg-purple-50" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low", color: "bg-gray-100 text-gray-600" },
  { value: "normal", label: "Normal", color: "bg-blue-100 text-blue-600" },
  { value: "high", label: "High", color: "bg-orange-100 text-orange-600" },
  { value: "urgent", label: "Urgent", color: "bg-red-100 text-red-600" },
];

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  reviewed: "bg-amber-100 text-amber-700",
  responded: "bg-green-100 text-green-700",
  resolved: "bg-gray-100 text-gray-600"
};

export const FeedbackSuggestionsTab = () => {
  const [feedbackType, setFeedbackType] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("normal");
  const [submitting, setSubmitting] = useState(false);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchMySubmissions();
  }, []);

  const fetchMySubmissions = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/feedback/my-submissions`);
      setMySubmissions(res.data.feedback || []);
    } catch (error) {
      console.error("Error fetching submissions:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedbackType) {
      toast.error("Please select a feedback type");
      return;
    }
    if (!subject.trim()) {
      toast.error("Please enter a subject");
      return;
    }
    if (!message.trim()) {
      toast.error("Please enter your message");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback/submit`, {
        feedbackType,
        subject,
        message,
        priority
      });
      
      toast.success("Thank you for your feedback! We'll review it shortly.");
      
      // Reset form
      setFeedbackType("");
      setSubject("");
      setMessage("");
      setPriority("normal");
      
      // Refresh submissions
      fetchMySubmissions();
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedType = FEEDBACK_TYPES.find(t => t.value === feedbackType);

  return (
    <div className="space-y-6">
      {/* Submit Feedback Card */}
      <Card className="border-2 border-indigo-200">
        <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <MessageCircle className="w-6 h-6" />
            Feedback & Suggestions
          </CardTitle>
          <CardDescription className="text-indigo-100">
            Share your thoughts, report issues, or suggest improvements. We value your input!
          </CardDescription>
        </CardHeader>
        
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Feedback Type Selection */}
            <div className="space-y-3">
              <Label className="text-indigo-700 font-semibold">What type of feedback do you have?</Label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {FEEDBACK_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFeedbackType(type.value)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                        feedbackType === type.value
                          ? `border-indigo-500 ${type.bgColor} ring-2 ring-indigo-300`
                          : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className={`w-6 h-6 ${feedbackType === type.value ? type.color : 'text-gray-400'}`} />
                      <span className={`text-xs text-center font-medium ${
                        feedbackType === type.value ? type.color : 'text-gray-600'
                      }`}>
                        {type.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject" className="text-indigo-700 font-semibold">Subject</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief summary of your feedback..."
                className="border-indigo-200 focus:border-indigo-400"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message" className="text-indigo-700 font-semibold">Your Message</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Please provide details about your feedback, suggestion, or issue..."
                className="border-indigo-200 focus:border-indigo-400 min-h-[150px]"
              />
            </div>

            {/* Priority (for issues) */}
            {(feedbackType === "issue" || feedbackType === "question") && (
              <div className="space-y-2">
                <Label className="text-indigo-700 font-semibold">Priority Level</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger className="w-48 border-indigo-200">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${opt.color}`}>
                          {opt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-between items-center pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowHistory(!showHistory)}
                className="border-gray-300"
              >
                <History className="w-4 h-4 mr-2" />
                {showHistory ? "Hide" : "View"} My Submissions ({mySubmissions.length})
              </Button>
              
              <Button
                type="submit"
                disabled={submitting || !feedbackType || !subject.trim() || !message.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-8"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Feedback
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Submission History */}
      {showHistory && (
        <Card className="border border-gray-200">
          <CardHeader className="bg-gray-50 border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="w-5 h-5 text-gray-600" />
              My Previous Submissions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {loadingHistory ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="mt-2 text-gray-500">Loading...</p>
              </div>
            ) : mySubmissions.length === 0 ? (
              <p className="text-center py-8 text-gray-500">You haven't submitted any feedback yet.</p>
            ) : (
              <div className="space-y-3">
                {mySubmissions.map((item) => {
                  const typeInfo = FEEDBACK_TYPES.find(t => t.value === item.feedbackType);
                  const TypeIcon = typeInfo?.icon || MessageCircle;
                  
                  return (
                    <div key={item.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <TypeIcon className={`w-5 h-5 ${typeInfo?.color || 'text-gray-500'}`} />
                          <span className="font-medium">{item.subject}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={STATUS_COLORS[item.status] || STATUS_COLORS.new}>
                            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                          </Badge>
                          <span className="text-xs text-gray-400">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{item.message}</p>
                      
                      {/* Show response from admin if there is one */}
                      {item.adminResponse && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-xs text-green-700 font-medium mb-1">
                            Response from Support:
                          </p>
                          <p className="text-sm text-green-800">{item.adminResponse}</p>
                          {item.respondedAt && (
                            <p className="text-xs text-green-600 mt-1">
                              {new Date(item.respondedAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5" />
          <div>
            <p className="text-sm text-indigo-800 font-medium">Your feedback matters!</p>
            <p className="text-xs text-indigo-600 mt-1">
              We review all submissions and may reach out via email if we need more information. 
              Check back here to see responses to your feedback.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackSuggestionsTab;
